import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading, withRouterConfig } from '@angular/router';

import { provideTranslateLoader, provideTranslateService, TranslateNoOpLoader } from '@ngx-translate/core';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipConfig } from 'ngx-bootstrap/tooltip';
import { CookieModule } from 'ngx-cookie';

import { AUTH_SLIDES_ENABLED } from '@bn/auth';
import { AuthService } from '@bn/auth/services';
import { Configuration as PartnerPortalConfiguration } from '@bn/openapi/partner-portal';
import { APP_CONFIG, ENV_CONFIG, EXT_URL_CONFIG } from '@bn/primitives';
import { fontawesomeProviders } from '@bn/shared/font-awesome';
import { provideWebUi } from '@bn/web-ui';

import { environment } from '../environments/environment';

import { appRoutes } from './app.routes';
import { authErrorInterceptor, authHeaderInterceptor } from './core/interceptors';
import { provideLightTheme } from './core/services';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideHttpClient(withFetch(), withInterceptors([authHeaderInterceptor, authErrorInterceptor])),
    { provide: APP_CONFIG, useValue: { toastr: {}, currencyCode: 'USD', api: { refreshInterval: { jwt: [5, 'm'] } } } },
    // Partner portal ships no slides config, so the shared auth pages must not fetch /data/slides-*.json
    { provide: AUTH_SLIDES_ENABLED, useValue: false },
    { provide: ENV_CONFIG, useValue: { apiUrl: environment.apiUrl, libsUrl: environment.libsUrl } },
    { provide: EXT_URL_CONFIG, useValue: {} },
    {
      provide: PartnerPortalConfiguration,
      useFactory: (auth: AuthService): PartnerPortalConfiguration =>
        new PartnerPortalConfiguration({
          basePath: environment.apiUrl,
          credentials: { bearer: (): string | undefined => auth.authStoreValue?.auth_token ?? undefined },
        }),
      deps: [AuthService],
    },
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'disabled' }),
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always', onSameUrlNavigation: 'reload' }),
      withPreloading(PreloadAllModules),
    ),
    importProvidersFrom(CookieModule.withOptions(), ModalModule),
    // Shared design-system providers (echarts setup, skeleton loader, chart pipes).
    // The world map is dashboard-only, so PP never fetches the country geometry.
    provideWebUi({ worldMap: false }),
    // PP has no dark design, so the theme is pinned to light rather than following the account setting.
    provideLightTheme(),
    // Must stay after provideWebUi() so PP's tooltip triggers override the web-ui defaults.
    {
      provide: TooltipConfig,
      // 'hover' expands to mouseover/mouseout, which bubble from child elements and make the
      // tooltip hide/re-show on every inner boundary crossing; mouseenter/mouseleave don't bubble.
      useFactory: (): TooltipConfig =>
        Object.assign(new TooltipConfig(), { adaptivePosition: false, placement: 'bottom', container: 'body', triggers: 'mouseenter:mouseleave focus' }),
    },
    fontawesomeProviders.provideFontawesome(),
    provideTranslateService({ loader: provideTranslateLoader(TranslateNoOpLoader) }),
  ],
};
